import React, { FC, useCallback, useEffect, useState } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import styled from 'styled-components';
import Colours from '../../../design-system/colours';
import OvButton from '../atoms/OvButton';
import Variables from '../../../design-system/variables';
import OvLoadingIndicator from '../atoms/OvLoadingIndicator';
import { useDropzone } from 'react-dropzone';
import { useTranslation } from 'react-i18next';

interface OvImageLocalUploadDialogProps {
  isOpen: boolean;
  onCancel: () => void;
  title: string;
  onLocalFileSelected: (res: { localFile: File; localUrl: string }) => void;
}

const OvImageLocalUploadDialog: FC<OvImageLocalUploadDialogProps> = ({
  isOpen,
  onCancel,
  title,
  onLocalFileSelected,
}) => {
  const { t } = useTranslation();
  const [file, setFile] = useState<File | null>(null);
  const [errorMessage, setErrorMessage] = useState<string>();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [preview, setPreview] = useState<string | null>(null);

  // Dropzone for picking files
  const onDrop = useCallback((acceptedFiles: File[]) => {
    if (acceptedFiles.length) {
      setFile(acceptedFiles[0]);
    }
  }, []);

  const { getRootProps, getInputProps, isFocused, isDragAccept, isDragReject } =
    useDropzone({
      accept: { 'image/*': [] },
      onDrop,
    });

  // Reset states when dialog closes
  useEffect(() => {
    if (!isOpen) {
      setFile(null);
      setPreview(null);
      setErrorMessage(undefined);
      setIsLoading(false);
    }
  }, [isOpen]);

  // Generate local preview
  useEffect(() => {
    if (!file) return;
    const objectUrl = URL.createObjectURL(file);
    setPreview(objectUrl);

    return () => {};
  }, [file]);

  // Called on form submit
  const handleConfirm = (e: React.FormEvent) => {
    e.preventDefault();
    setErrorMessage(undefined);

    if (!file || !preview) {
      setErrorMessage('Please select a file first');
      return;
    }

    // Maybe show a short spinner
    setIsLoading(true);
    setTimeout(() => {
      setIsLoading(false);

      onLocalFileSelected({
        localFile: file,
        localUrl: preview,
      });
      onCancel();
    }, 500);
  };

  return (
    <Dialog open={isOpen} onClose={onCancel}>
      <StyledDialogContent>
        <StyledDialogTitle>{title}</StyledDialogTitle>
        <form onSubmit={handleConfirm}>
          <StyledContainer>
            {file && preview ? (
              <StyledImage src={preview} alt="Local Preview" />
            ) : (
              <DropzoneContainer
                {...getRootProps({ isFocused, isDragAccept, isDragReject })}
              >
                <input {...getInputProps()} />
                <p>{t('imageUpload.dragNDropText')}</p>
              </DropzoneContainer>
            )}

            <StyledSubmitButton type="submit" disabled={!file}>
              {t('scans.uploadImageCta')}
            </StyledSubmitButton>

            {errorMessage && (
              <StyledErrorMessage>{errorMessage}</StyledErrorMessage>
            )}
            {isLoading && <OvLoadingIndicator position="fixed" />}
          </StyledContainer>
        </form>
      </StyledDialogContent>
    </Dialog>
  );
};

export default OvImageLocalUploadDialog;

/** --- Styles --- */
const StyledDialogTitle = styled.h2`
  color: ${Colours.OV_BASE};
`;

const StyledDialogContent = styled(DialogContent)`
  && {
    text-align: center;
  }
`;

const StyledContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const DropzoneContainer = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 1.25rem;
  border-radius: ${Variables.borderRadius.MEDIUM};
  background-color: ${Colours.OV_LIGHT_GRAY};
  color: ${Colours.OV_BASE};
  outline: none;
  cursor: pointer;
`;

const StyledSubmitButton = styled(OvButton)`
  && {
    border-radius: ${Variables.borderRadius.XLARGE};
    padding: 0.5rem 0.5rem;
    background-color: ${Colours.OV_BASE};
    margin: 2rem 0;
    width: 100%;

    &:disabled {
      background-color: ${Colours.OV_GRAY};
      color: ${Colours.OV_LIGHT_GRAY};
    }
  }
`;

const StyledErrorMessage = styled.p`
  color: ${Colours.OV_RED};
  margin: 0.625rem 0;
`;

const StyledImage = styled.img`
  width: 100%;
  height: auto;
  border-radius: 5%;
  margin-bottom: 0.25rem;
  overflow: hidden;
  object-fit: cover;
`;
